import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { BreakdownBy } from '../components/breakdown-by';
import { applyGroup } from '@/store/atsReports/rejections';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTable,
  selectTableLoading,
  selectRejectionsReportsGroup,
} from '@/store/atsReports/rejections/selectors';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

interface Option {
  label: string;
  id: number | string;
}

const groupBy = [
  { id: null, label: 'Time', key: 'dt' },
  { id: 'job_name', label: 'Job name', key: 'g1.name' },
  { id: 'rejected_stage', label: 'Job stage', key: 'g1.name' },
  { id: 'primary_recruiter', label: 'Primary recruiter', key: 'g1.name' },
  { id: 'primary_hm', label: 'Primary hiring manager', key: 'g1.name' },
  {
    id: 'appl_contact_source',
    label: 'Source (on application)',
    key: 'g1?.name',
  },
  { id: 'department', label: 'Department', key: 'g1.name' },
  { id: 'rejection_type', label: 'Rejection type', key: 'g1.name' },
  { id: 'rejection_reason', label: 'Rejection reason', key: 'g1.name' },
  { id: 'rejected_by', label: 'Applicant rejected by', key: 'g1.name' },
  { id: 'rejected_by_type', label: 'Rejected by', key: 'g1.name' },
];

const breakDownBy = [
  { id: null, label: 'None' },
  { id: 'job_name', label: 'Job name' },
  { id: 'rejected_stage', label: 'Job stage' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'appl_contact_source', label: 'Source (on application)' },
  { id: 'department', label: 'Department' },
  { id: 'rejection_type', label: 'Rejection type' },
  { id: 'rejection_reason', label: 'Rejection reason' },
  { id: 'rejected_by', label: 'Applicant Rejected by' },
  { id: 'rejected_by_type', label: 'Rejected by' },
];

export const TableView = ({
  groupBy1 = undefined,
  groupBy2 = undefined,
  customData = undefined,
  isReadOnly = false,
  isPrintView = false,
  customBreakdownChange = undefined,
  renderTitle = undefined,
  onDrillDownOpen = (_data) => {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { consumer } = useContext(ReportSelectionContext);
  const reduxIsLoading = useSelector(selectTableLoading);
  const reduxRejectionTable = useSelector(selectTable);
  const reduxGroup = useSelector(selectRejectionsReportsGroup);

  const isTableLoading = feReportsV3 ? false : reduxIsLoading;
  const rejectionTable = feReportsV3 ? [] : reduxRejectionTable;
  const group = feReportsV3 ? null : reduxGroup;
  const { feDrilldown } = useKFlags();
  const dataToUse = customData || rejectionTable;
  const isDataEmpty = isReportTableEmpty(dataToUse, 'rejections');
  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);
  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, group?.group_by_1),
    group_by_2: getGroupValue(groupBy2, group?.group_by_2),
  };
  /* eslint-disable react-hooks/preserve-manual-memoization -- deps intentionally narrow */
  const columns = useMemo(() => {
    const header = feReportsV3
      ? groupBy1 || groupBy[0]
      : groupBy1 || group?.group_by_1 || groupBy[0];
    const formatRejectedByTypeValue = (value: string) => {
      const rejectedByTypeMap: Record<string, string> = {
        User: 'User',
        AtsAutoRejectionRule: 'Auto-rejection rule',
      };
      return rejectedByTypeMap[value] || value;
    };

    const headerColumn = renderHeader({
      accessor: 'header',
      title: header?.label || 'Name',
      width: 400,
      expand: false,
      sort: true,
      isReadOnly,
      groupBy: group?.group_by_1?.id,
      breakDownBy: group?.group_by_2?.id,
      renderFooter: () => {
        return <KText size="sm-semi-bold">Total</KText>;
      },
    });

    const customHeaderColumn = {
      ...headerColumn,
      id: 'header',
      title: header?.label || 'Name',
      columnConfig: false,
      Cell: (props: any) => {
        const formattedValue = formatRejectedByTypeValue(props.cell.value);

        const modifiedProps = {
          ...props,
          cell: {
            ...props.cell,
            value: formattedValue,
          },
        };

        return headerColumn.Cell(modifiedProps);
      },
    };

    return [
      customHeaderColumn,

      {
        ...renderCell({
          title: 'Rejections',
          accessor: 'rejections',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Rejections',
                    false,
                    drilldownGroup,
                    null,
                    row?.rejections
                  )
                }
              >
                {row?.rejections}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Rejections',
                    true,
                    drilldownGroup,
                    null,
                    row?.rejections_total
                  )
                }
              >
                <KText size="sm-semi-bold">{row?.rejections_total}</KText>
              </Flex>
            );
          },
        }),
        id: 'rejections',
        title: 'Rejections',
        columnConfig: true,
      },
      {
        ...renderCell({
          title: 'Avg. time to reject',
          accessor: 'time_to_reject',
          width: 100,
          sort: true,
          render: (_, row) => {
            const avgTimeToReject =
              parseInt(row?.time_to_reject) / parseInt(row?.rejections);
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor="pointer"
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Time to reject',
                    false,
                    drilldownGroup,
                    null,
                    secondsToHumanTime(avgTimeToReject)
                  )
                }
              >
                {secondsToHumanTime(avgTimeToReject)}
              </Flex>
            );
          },
        }),
        id: 'time_to_reject',
        title: 'Avg. time to reject',
        columnConfig: true,
        sortType: (rowA, rowB) => {
          const avgA =
            rowA.original?.rejections && parseInt(rowA.original?.rejections) > 0
              ? parseInt(rowA.values?.time_to_reject) /
                parseInt(rowA.original?.rejections)
              : 0;
          const avgB =
            rowB.original?.rejections && parseInt(rowB.original?.rejections) > 0
              ? parseInt(rowB.values?.time_to_reject) /
                parseInt(rowB.original?.rejections)
              : 0;
          return avgA - avgB;
        },
      },
    ];
  }, [groupBy1, groupBy2, feReportsV3, group]); //eslint-disable-line
  /* eslint-enable react-hooks/preserve-manual-memoization */

  const handleBreakdownChange = (key: string, option: Option) => {
    switch (key) {
      case 'group_by_1':
        dispatch(applyGroup({ key: 'group_by_1', value: option }));

        break;
      case 'group_by_2':
        dispatch(applyGroup({ key: 'group_by_2', value: option }));
        break;
      default:
        break;
    }
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: groupBy,
      selected: feReportsV3
        ? groupBy1 || groupBy[0]
        : groupBy1 || group?.group_by_1 || groupBy[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: breakDownBy,
      selected: feReportsV3
        ? groupBy2 || breakDownBy[0]
        : groupBy2 || group?.group_by_2 || breakDownBy[0],
    },
  ];

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  if (isFilterApplied) {
    return (
      <EmptyReportChart
        text="Filters not applicable"
        subText="The applied filters are not applicable for this block"
      />
    );
  }

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle?.()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Rejection breakdown"
                metricDefinition="Comprehensive breakdown of the rejection data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                  ? '15px'
                  : isEditMode
                    ? '55px'
                    : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field="rejections">
          <Table
            tableId="rejection-table"
            skipLocalStorage={skipLocalStorage}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            tableColStyles={tableColStyles}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            defaultSortBy={[{ id: 'header', desc: false }]}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
